// SOVRANOVA — shared voice-safety hook + crisis takeover.
// Live voice connects the browser straight to Hume, bypassing the server-side
// SafetyService. This hook closes that gap AND makes the safeguard act on the
// spoken channel: it screens each new user utterance via /api/safety/screen
// (same lane as text); on a detected crisis it SILENCES Hume immediately
// (pauseAssistant + muteAudio + setVolume 0) and SPEAKS SOVRANOVA's own crisis
// response ALOUD via /api/voice/speak, then returns crisis info for the visual
// <CrisisNotice/> backup. Voice-first: a distressed user may not be looking at
// the screen, so a silent box is not enough — it must be heard and Hume must stop.
// The hook reads the silence controls from useVoice() itself, so every surface
// already calling useVoiceSafety(messages) inside its <VoiceProvider> gets the
// takeover with NO change to its own code. The takeover latches (no auto-resume).
// Vent does NOT use this hook — it screens via /vent/message (humeMode).
import { useEffect, useRef, useState } from 'react'
import { getCrisisResources, resolveRegion, HELPLINE_DIRECTORY_URL } from '../services/crisisResources'
import { useVoice } from '@humeai/voice-react'
import { api } from '../services/api'

// Speak a line aloud in SOVRANOVA's own voice (ElevenLabs via /api/voice/speak),
// the same path the voice surfaces use for their spoken opener.
async function speakAloud(text) {
  if (!text) return
  try {
    const res = await api.post('/voice/speak', { text })
    if (!res?.audio) return
    const bin = atob(res.audio)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    const url = URL.createObjectURL(new Blob([bytes], { type: res.mimeType || 'audio/mpeg' }))
    new Audio(url).play().catch(() => {})
  } catch (_) {
    // Best-effort: a paused/muted Hume + the visual notice still stand.
  }
}

// Turn the server crisisResponse into a clean spoken line (no bullet dashes).
function forSpeech(t) {
  return (t || '').split('\n').map(l => l.replace(/^\s*-\s*/, '')).filter(l => l.trim().length).join('. ')
}

// Spoken fallback when the server's crisisResponse is unavailable: built from
// the SAME deterministic registry as the visual notice, for the person's own
// region (unknown → GLOBAL). Never a hard-coded country.
function fallbackSpoken() {
  const r = getCrisisResources(resolveRegion())
  const primary = r.primary.tel ? `${r.primary.label} on ${r.primary.number}` : `${r.primary.label}: ${r.primary.number}`
  return "I'm really glad you're here, and I don't want you to go through this alone. " +
    `Please reach out for support right now — ${primary}, or ${r.emergency.number} in an emergency. ` +
    "Support is available right now."
}

export function useVoiceSafety(messages) {
  const { pauseAssistant, muteAudio, setVolume } = useVoice()
  const screened = useRef(0)
  const tookOver = useRef(false)
  const [crisis, setCrisis] = useState(null)

  useEffect(() => {
    if (!messages?.length) return
    const userMsgs = messages.filter(m => m.type === 'user_message' && m.message?.content)
    if (userMsgs.length <= screened.current) return
    const fresh = userMsgs.slice(screened.current)
    screened.current = userMsgs.length

    // Act on a confirmed-unsafe screen result: show the notice, then take over
    // the spoken channel ONCE — silence Hume (pause + mute + volume 0) and speak
    // SOVRANOVA's crisis response aloud (voice-first: it must be heard).
    const runTakeover = (r) => {
      setCrisis(r)
      if (tookOver.current) return
      tookOver.current = true
      try { pauseAssistant && pauseAssistant() } catch (_) {}
      try { muteAudio && muteAudio() } catch (_) {}
      try { setVolume && setVolume(0) } catch (_) {}
      speakAloud(forSpeech(r.crisisResponse) || fallbackSpoken())
    }

    // Screen one utterance. A transient network blip must NOT silently drop a
    // crisis screen, so retry once before giving up. We deliberately do NOT
    // fabricate a crisis on persistent failure (that would wrongly silence Hume
    // and alarm a non-crisis user); the always-visible crisis resources are the net.
    const screen = async (text) => {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          // D0-G4: send the person's own region. Without it the server
          // resolved GLOBAL and SPOKE "find a helpline in your country" instead
          // of their actual crisis line — on the one channel where the reply is
          // heard rather than read. resolveRegion() is the same offline-safe
          // resolver the visible CrisisNotice and the lifeline pill already use
          // (explicit setting → profile → locale → timezone → GLOBAL).
          const r = await api.post('/safety/screen', { text, region: resolveRegion() })
          if (r && r.safe === false) runTakeover(r)
          return
        } catch (_) {
          if (attempt === 0) await new Promise(res => setTimeout(res, 1500))
        }
      }
    }

    fresh.forEach(m => { screen(m.message.content) })
  }, [messages, pauseAssistant, muteAudio, setVolume])

  return crisis
}

export function CrisisNotice({ crisis }) {
  if (!crisis) return null
  return (
    <div style={{ background:'rgba(180,40,60,0.14)', border:'1px solid rgba(225,90,110,0.5)', borderRadius:12, padding:'0.9rem 1rem', margin:'0.6rem 0', color:'#fff', textAlign:'left' }}>
      <p style={{ fontWeight:600, margin:'0 0 0.4rem' }}>You don't have to face this alone.</p>
      {crisis.crisisResponse && (
        <div style={{ whiteSpace:'pre-wrap', fontSize:'0.92rem', lineHeight:1.45, marginBottom:'0.6rem' }}>{crisis.crisisResponse}</div>
      )}
      <CrisisContacts />
    </div>
  )
}

// Region-aware contacts. Falls back to the global helpline directory rather than
// to any one country's numbers — an unplaceable user must never be shown "000".
export function CrisisContacts({ linkColor = '#ffd6dc' }) {
  const res = getCrisisResources(resolveRegion())
  const link = { color: linkColor, fontWeight: 700 }
  return (
    <>
      <div style={{ display:'flex', gap:'0.9rem', flexWrap:'wrap' }}>
        {res.primary.tel
          ? <a href={`tel:${res.primary.tel}`} style={link}>Call {res.primary.label} {res.primary.number}</a>
          : <a href={HELPLINE_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" style={link}>{res.primary.label}</a>}
        {res.emergency.tel
          ? <a href={`tel:${res.emergency.tel}`} style={link}>Emergency {res.emergency.number}</a>
          : <span style={{ ...link, opacity:0.9 }}>Emergency — {res.emergency.number}</span>}
      </div>
      {res.text && <p style={{ margin:'0.4rem 0 0', fontSize:'0.85rem', opacity:0.9 }}>{res.text}</p>}
      <p style={{ margin:'0.35rem 0 0', fontSize:'0.8rem', opacity:0.8 }}>
        Not in {res.region}?{' '}
        <a href={HELPLINE_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" style={{ color: linkColor, textDecoration:'underline' }}>
          Find support in your country
        </a>{' '}· or go to your nearest emergency department.
      </p>
    </>
  )
}
